import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { PageTitle, useLayout } from '../../../_metronic/layout/core';
import { DefaultTitle } from '../../../_metronic/layout/components/header/page-title/DefaultTitle';
import EmpresasService from '../../services/EmpresasService';
import RelatoriosService from '../../services/RelatoriosService';
import gerenciarSaldoInicialService, {
    ISaldoInicialLancamento,
    ISaldoInicialLancamentoFilter,
} from '../../services/gerenciarSaldoInicialService';
import { IEsteira } from '../../interfaces/IEsteira';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const PER_PAGE = 20;

const filtroVazio: ISaldoInicialLancamentoFilter = { codigoContabil: '' };

function GerenciarSaldoInicial() {
    const history = useHistory();
    const location = useLocation();
    const { classes } = useLayout();

    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const empresaIdInicial = Number(queryParams.get('empresaId')) || 0;
    const esteiraIdInicial = Number(queryParams.get('esteiraId')) || 0;

    const [empresas, setEmpresas] = useState<any[]>([]);
    const [esteiras, setEsteiras] = useState<IEsteira[]>([]);
    const [empresaId, setEmpresaId] = useState<number>(empresaIdInicial);
    const [esteiraId, setEsteiraId] = useState<number>(esteiraIdInicial);

    const [filtros, setFiltros] = useState<ISaldoInicialLancamentoFilter>(filtroVazio);
    const [items, setItems] = useState<ISaldoInicialLancamento[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [desfazendoTodos, setDesfazendoTodos] = useState(false);
    const [desfazendoId, setDesfazendoId] = useState<number | null>(null);

    useEffect(() => {
        // Mesma fonte usada em /andamento-contas (GET Empresa?perPage=400) — obterTodosPorUsuarioId
        // escopa por permissão explícita de usuário e pode não incluir a empresa vinda do redirect.
        RelatoriosService.obterEmpresasPorClienteID(0, (resp: any[]) => setEmpresas(resp));
    }, []);

    useEffect(() => {
        if (!empresaId) {
            setEsteiras([]);
            return;
        }
        EmpresasService.obterEsteiras(empresaId).then(setEsteiras);
    }, [empresaId]);

    const carregar = useCallback((pg: number, f: ISaldoInicialLancamentoFilter) => {
        if (!esteiraId) return;
        setLoading(true);
        gerenciarSaldoInicialService.listar(esteiraId, { ...f, page: pg, perPage: PER_PAGE })
            .then(resp => {
                setItems(resp.items);
                setTotal(resp.total);
                setTotalPages(resp.totalPages);
                setPage(pg);
            })
            .finally(() => setLoading(false));
    }, [esteiraId]);

    useEffect(() => {
        if (esteiraId) carregar(1, filtroVazio);
        else { setItems([]); setTotal(0); setTotalPages(1); }
    }, [esteiraId, carregar]);

    // Poll enquanto houver um DESFAZER_SALDO_INICIAL em andamento pra esteira — mesmo padrão do
    // polling de Transportar Saldo em /andamento-contas. Só recarrega a listagem quando a operação
    // ACABA DE terminar (transição true -> false), não a cada tick — senão a lista reseta pra
    // página 1 a cada 8s mesmo sem nada acontecendo.
    const emAndamentoAnteriorRef = useRef(false);
    useEffect(() => {
        if (!esteiraId) return;
        let ativo = true;
        emAndamentoAnteriorRef.current = false;

        const checar = async () => {
            try {
                const ops = await EmpresasService.buscarOperacoesEsteira(esteiraId);
                if (!ativo) return;

                const emAndamento = ops.some((o: any) => o.tipo === 'DESFAZER_SALDO_INICIAL' && o.processando);
                setDesfazendoTodos(emAndamento);

                if (!emAndamento && emAndamentoAnteriorRef.current) carregar(1, filtros);
                emAndamentoAnteriorRef.current = emAndamento;
            } catch { }
        };

        checar();
        const interval = setInterval(checar, 8000);
        return () => { ativo = false; clearInterval(interval); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [esteiraId]);

    const setF = (patch: Partial<ISaldoInicialLancamentoFilter>) => setFiltros(prev => ({ ...prev, ...patch }));
    const onBuscar = () => carregar(1, filtros);
    const onLimpar = () => { setFiltros(filtroVazio); carregar(1, filtroVazio); };
    const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !loading) onBuscar(); };

    const handleDesfazerUm = async (item: ISaldoInicialLancamento) => {
        const confirm = await Swal.fire({
            title: 'Desfazer lançamento de saldo inicial?',
            html: `Conta <b>${item.codigoContabil}</b> — ${BRL.format(item.valorProvisaoRecolher ?? 0)}<br/>O lançamento será excluído definitivamente.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Desfazer',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
        });
        if (!confirm.isConfirmed) return;

        setDesfazendoId(item.id);
        try {
            await gerenciarSaldoInicialService.desfazerUm(item.id);
            Swal.fire({ text: 'Lançamento desfeito.', icon: 'success', timer: 2500, showConfirmButton: false });
            carregar(page, filtros);
        } catch (e: any) {
            Swal.fire({ text: e?.response?.data?.message ?? 'Erro ao desfazer o lançamento.', icon: 'error', timer: 3000, showConfirmButton: false });
        } finally {
            setDesfazendoId(null);
        }
    };

    const handleDesfazerTodos = async () => {
        const confirm = await Swal.fire({
            title: 'Desfazer todos os lançamentos de saldo inicial?',
            html: `${total} lançamento${total !== 1 ? 's' : ''} ser${total !== 1 ? 'ão' : 'á'} excluído${total !== 1 ? 's' : ''} definitivamente${filtros.codigoContabil ? ` (conta ${filtros.codigoContabil})` : ''}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Desfazer todos',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
        });
        if (!confirm.isConfirmed) return;

        try {
            await gerenciarSaldoInicialService.desfazerTodos(esteiraId, filtros.codigoContabil || undefined);
            setDesfazendoTodos(true);
            Swal.fire({ text: 'Desfazimento enviado para processamento.', icon: 'success', timer: 3000, showConfirmButton: false });
        } catch (e: any) {
            const msg = e?.response?.data?.message ?? 'Erro ao iniciar o desfazimento.';
            Swal.fire({ text: msg, icon: 'error', timer: 3000, showConfirmButton: false });
        }
    };

    return (
        <>
            <PageTitle breadcrumbs={[]}>Gerenciar Saldo Inicial</PageTitle>
            <div className='toolbar' id='kt_toolbar'>
                <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                    <DefaultTitle />
                </div>
            </div>

            <div className='card'>
                <div className='card-header border-0 pt-6'>
                    <div className='card-title'>
                        <span className='text-muted fs-7'>
                            Lançamentos com saldo inicial marcado, ou lançados em ano anterior ao período da esteira. Desfazer exclui o lançamento definitivamente.
                        </span>
                    </div>
                    <div className='card-toolbar'>
                        <button className='btn btn-sm btn-light' onClick={() => history.goBack()}>‹ Voltar</button>
                    </div>
                </div>

                <div className='card-body pt-0'>
                    <div className='d-flex align-items-end gap-3 flex-wrap mb-5'>
                        <div>
                            <label className='form-label fs-7 fw-semibold mb-1'>Empresa</label>
                            <select
                                className='form-select form-select-sm'
                                style={{ minWidth: 220 }}
                                value={empresaId || ''}
                                onChange={e => { setEmpresaId(Number(e.target.value) || 0); setEsteiraId(0); }}
                            >
                                <option value=''>Selecione</option>
                                {empresas.map(e => (
                                    <option key={e.id} value={e.id}>{e.razaoSocial || e.nomeFantasia}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className='form-label fs-7 fw-semibold mb-1'>Esteira (importação)</label>
                            <select
                                className='form-select form-select-sm'
                                style={{ minWidth: 180 }}
                                value={esteiraId || ''}
                                disabled={!empresaId}
                                onChange={e => setEsteiraId(Number(e.target.value) || 0)}
                            >
                                <option value=''>Selecione</option>
                                {esteiras.map(e => (
                                    <option key={e.id} value={e.id}>
                                        {moment(e.periodoInicio).format('MM/YYYY')} a {moment(e.periodoFim).format('MM/YYYY')}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className='form-label fs-7 fw-semibold mb-1'>Código contábil</label>
                            <input
                                className='form-control form-control-sm'
                                style={{ width: 160 }}
                                placeholder='Opcional'
                                value={filtros.codigoContabil ?? ''}
                                onChange={e => setF({ codigoContabil: e.target.value })}
                                onKeyDown={onKeyDown}
                            />
                        </div>

                        <button className='btn btn-sm btn-primary' onClick={onBuscar} disabled={loading || !esteiraId}>
                            {loading ? <span className='spinner-border spinner-border-sm' role='status' /> : 'Buscar'}
                        </button>
                        <button className='btn btn-sm btn-light' onClick={onLimpar} disabled={loading || !esteiraId}>Limpar</button>

                        <button
                            className='btn btn-sm btn-danger ms-auto'
                            disabled={!esteiraId || total === 0 || desfazendoTodos}
                            onClick={handleDesfazerTodos}
                        >
                            {desfazendoTodos
                                ? <><span className='spinner-border spinner-border-sm me-1' />Desfazendo...</>
                                : 'Desfazer todos'}
                        </button>
                    </div>

                    {!esteiraId ? (
                        <div className='text-center text-muted py-8 fs-6'>Selecione uma empresa e uma esteira para consultar.</div>
                    ) : loading && items.length === 0 ? (
                        <div className='d-flex justify-content-center align-items-center py-10'>
                            <div className='spinner-border text-primary' role='status'>
                                <span className='visually-hidden'>Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <div className='table-responsive'>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4'>
                                <thead>
                                    <tr className='fw-bold text-muted bg-light'>
                                        <th className='min-w-100px ps-4 rounded-start'>Data</th>
                                        <th className='min-w-120px'>Conta</th>
                                        <th className='min-w-100px text-end'>Valor</th>
                                        <th className='min-w-60px'>Tipo</th>
                                        <th className='min-w-80px text-center'>Ano</th>
                                        <th className='min-w-160px'>Origem</th>
                                        <th className='min-w-100px text-end pe-4 rounded-end'>Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className='text-center text-muted py-8 fs-6'>
                                                Nenhum lançamento de saldo inicial encontrado.
                                            </td>
                                        </tr>
                                    ) : (
                                        items.map(item => (
                                            <tr key={item.id}>
                                                <td className='ps-4 fs-7'>{moment(item.dataLancamento).format('DD/MM/YYYY')}</td>
                                                <td className='fs-7 fw-semibold'>{item.codigoContabil}</td>
                                                <td className='text-end fs-7 fw-semibold'>{BRL.format(item.valorProvisaoRecolher ?? 0)}</td>
                                                <td className='fs-7'>{item.tipoLancamento}</td>
                                                <td className='text-center fs-7'>{item.anoCompetencia ?? '—'}</td>
                                                <td className='fs-7 text-muted' style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    title={item.nomeArquivoOrigem ?? undefined}>
                                                    {item.nomeArquivoOrigem || '—'}
                                                </td>
                                                <td className='text-end pe-4'>
                                                    <button
                                                        className='btn btn-sm btn-light-danger'
                                                        disabled={desfazendoId === item.id}
                                                        onClick={() => handleDesfazerUm(item)}
                                                    >
                                                        {desfazendoId === item.id
                                                            ? <span className='spinner-border spinner-border-sm' role='status' />
                                                            : 'Desfazer'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {esteiraId && totalPages > 1 && (
                        <div className='d-flex justify-content-between align-items-center mt-3'>
                            <span className='fs-7 text-muted'>Página {page} de {totalPages} · {total} registro{total !== 1 ? 's' : ''}</span>
                            <div className='d-flex gap-1'>
                                <button className='btn btn-sm btn-light' disabled={page === 1 || loading} onClick={() => carregar(page - 1, filtros)}>‹</button>
                                <button className='btn btn-sm btn-light' disabled={page === totalPages || loading} onClick={() => carregar(page + 1, filtros)}>›</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default GerenciarSaldoInicial;
